import { createListenerMiddleware } from '@reduxjs/toolkit';

import { CrossOriginCommunication } from '../../controllers/crossOriginCommunication/CrossOriginCommunicationController';
import type { ICrossOriginCommunicator } from '../../controllers/crossOriginCommunication/types';
import { IntegrationApi, OutgoingMessage } from '../../controllers/crossOriginCommunication/types';
import { importCommunicator } from '../../controllers/crossOriginCommunication/utils';
import { Logger } from '../../logger/Logger';
import { fetchCasinoConfigAction } from '../apiActions/fetchCasinoConfigAction';
import { fetchUserInfoAction } from '../apiActions/fetchUserInfoAction';
import type { CoreRootState, CoreStoreType } from '../CoreStore';

export const crossOriginCommunicationMiddleware = createListenerMiddleware<CoreRootState>();

crossOriginCommunicationMiddleware.startListening({
  actionCreator: fetchCasinoConfigAction.fulfilled,
  effect: (_, store) => {
    const config = store.getState().config;
    const casinoConfig = config.casinoConfig;
    const integrationApi = casinoConfig?.integrationApi?.length
      ? casinoConfig.integrationApi
      : [IntegrationApi.Default];
    /**
     * if no configuration is coming from response
     * or it gets parsed to an empty array,
     * the default communicator needs to be initialized
     */
    integrationApi.forEach((integration) => {
      importCommunicator(integration)
        .then((communicator) => {
          initCommunicator(communicator, store as never);

          CrossOriginCommunication.dispatch(OutgoingMessage.GameLoadStart, {
            tableId: config.tableConfig?.operatorGameId,
            isResponsibleGamingEnabled:
              casinoConfig?.isMicroGamingEnabled && casinoConfig?.isResponsibleGamingEnabled && !(store as any).lobby,
          });
        })
        .catch((integration) => Logger.logFailure('integrationApi', { additional: integration }));
    });
  },
});

crossOriginCommunicationMiddleware.startListening({
  actionCreator: fetchUserInfoAction.fulfilled,
  effect: (_, store) => {
    CrossOriginCommunication.dispatch(OutgoingMessage.UpdateBalance, {
      balance: store.getState().gameState.userInfo?.balance,
    });
  },
});

crossOriginCommunicationMiddleware.startListening({
  predicate: (_, currentState, previousState) => currentState.bets?.totalBet !== previousState.bets?.totalBet,
  effect: (_, store) => {
    const state = store.getState();

    CrossOriginCommunication.dispatch(OutgoingMessage.UpdateBets, {
      balance: state.gameState.userInfo?.balance,
      totalBet: state.bets?.totalBet ?? 0,
    });
  },
});

crossOriginCommunicationMiddleware.startListening({
  predicate: (_, currentState, previousState) =>
    currentState.gameState.winResult?.amount !== previousState.gameState.winResult?.amount,
  effect: (_, store) => {
    const gameState = store.getState().gameState;

    CrossOriginCommunication.dispatch(OutgoingMessage.UpdateWin, {
      balance: gameState.userInfo?.balance,
      winResultAmount: gameState.winResult?.amount ?? 0,
    });
  },
});

crossOriginCommunicationMiddleware.startListening({
  predicate: (_, currentState, previousState) =>
    (currentState.settings.volume.master.value === 0 && previousState.settings.volume.master.value !== 0) ||
    (currentState.settings.volume.master.value !== 0 && previousState.settings.volume.master.value === 0),
  effect: (_, store) => {
    CrossOriginCommunication.dispatch(OutgoingMessage.UpdateSettings, {
      masterVolume: store.getState().settings.volume.master.value,
    });
  },
});

// Utils
const initCommunicator = (communicator: ICrossOriginCommunicator, store: CoreStoreType): void => {
  CrossOriginCommunication.setCommunicator(communicator);
  CrossOriginCommunication.setStore(store);
  CrossOriginCommunication.connect();
};
